import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import { UserPlus, Trash2, Users } from "lucide-react";

const ROLE_TONE = {
  admin: "bg-red-50 text-red-700",
  acquisition_manager: "bg-emerald-50 text-emerald-700",
  lead_manager: "bg-sky-50 text-sky-700",
  cold_caller: "bg-amber-50 text-amber-700",
};

export default function Team({ hideHeader = false, forceInviteOpen = false, onInviteOpenChange = null }) {
  const { user, can, role } = useAuth();
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [roles, setRoles] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", role: "cold_caller" });
  const canManage = can ? can("team.manage") : (user?.role === "admin");

  // Allow parent (TeamHub) to open the invite dialog via a prop.
  useEffect(() => {
    if (forceInviteOpen) setOpen(true);
  }, [forceInviteOpen]);
  const setOpenControlled = (v) => {
    setOpen(v);
    if (!v && onInviteOpenChange) onInviteOpenChange(false);
  };

  const load = async () => {
    const [{ data: rx }, { data: mx }, { data: ix }] = await Promise.all([
      api.get("/team/roles"),
      api.get("/team/members"),
      canManage ? api.get("/team/invites") : Promise.resolve({ data: { items: [] } }),
    ]);
    setRoles(rx.roles || []);
    setMembers(mx || []);
    setInvites(ix.items || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const sendInvite = async () => {
    if (!form.email.trim()) return toast.error("Email required");
    try {
      const { data } = await api.post("/team/invite", form);
      toast.success(`Invite sent to ${form.email}`);
      // Fallback copy-link if email delivery failed silently
      if (data.invite_url) {
        try { await navigator.clipboard.writeText(data.invite_url); } catch { /* ignore */ }
      }
      setOpen(false);
      setForm({ email: "", name: "", role: "cold_caller" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invite failed");
    }
  };

  const changeRole = async (uid, role) => {
    try {
      await api.patch(`/team/members/${uid}/role`, { role });
      toast.success("Role updated");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const remove = async (uid, email) => {
    if (!window.confirm(`Remove ${email} from the team?`)) return;
    try {
      await api.delete(`/team/members/${uid}`);
      toast.success("Removed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Remove failed");
    }
  };

  return (
    <div className="p-6 md:p-10 max-w-[1400px]" data-testid="team-page">
      <div className="flex items-end justify-between mb-6 reveal flex-wrap gap-3">
        {!hideHeader && (
          <div>
            <span className="app-eyebrow">/ Team</span>
            <h1 className="font-display text-4xl md:text-5xl mt-3">Team &amp; roles</h1>
            <p className="text-[var(--app-text-muted)] text-sm mt-2 max-w-lg">
              Invite cold callers, lead managers, and acquisition managers.
              Everyone sees the full pipeline; role controls what each person
              can edit.
            </p>
          </div>
        )}
        {hideHeader && <div className="flex-1" />}
        {canManage && (
          <Dialog open={open} onOpenChange={setOpenControlled}>
            <DialogTrigger asChild>
              <button className="app-btn app-btn-primary" data-testid="invite-btn">
                <UserPlus size={14} /> Invite teammate
              </button>
            </DialogTrigger>
            <DialogContent className="rounded-2xl" data-testid="invite-dialog">
              <DialogHeader>
                <DialogTitle>Invite a teammate</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>Email</Label>
                  <Input
                    data-testid="invite-email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="field-sharp mt-1"
                    placeholder="jane@example.com"
                  />
                </div>
                <div>
                  <Label>Name (optional)</Label>
                  <Input
                    data-testid="invite-name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="field-sharp mt-1"
                    placeholder="Jane Doe"
                  />
                </div>
                <div>
                  <Label>Role</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger data-testid="invite-role" className="field-sharp mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {roles.map((r) => (
                        <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <button
                  onClick={sendInvite}
                  className="app-btn app-btn-primary"
                  data-testid="send-invite"
                >
                  Send invite
                </button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="card-flat" data-testid="members-list">
        <div className="px-5 py-3 border-b border-[var(--app-line)] text-[10px] uppercase tracking-[0.2em] text-[var(--app-text-muted)] flex items-center gap-2">
          <Users size={12} /> {members.length} active member{members.length === 1 ? "" : "s"}
        </div>
        {members.length === 0 && canManage && (
          <div className="p-8 md:p-12 flex flex-col items-center text-center gap-4"
               data-testid="empty-team-cta">
            <div className="w-14 h-14 rounded-full bg-[var(--app-surface)] flex items-center justify-center">
              <UserPlus size={22} />
            </div>
            <div>
              <div className="font-display text-xl font-bold">Add your first teammate</div>
              <p className="text-sm text-[var(--app-text-muted)] mt-1 max-w-md">
                Invite cold callers, lead managers or an acquisition manager.
                They&apos;ll get an email with a signup link scoped to their role.
              </p>
            </div>
            <button
              onClick={() => setOpen(true)}
              className="app-btn app-btn-primary"
              data-testid="empty-invite-btn"
            >
              <UserPlus size={14} /> Invite teammate
            </button>
          </div>
        )}
        {members.length === 0 && !canManage && (
          <div className="p-8 text-center text-sm text-[var(--app-text-muted)]">
            No teammates yet. Only admins can invite new members.
          </div>
        )}
        {members.map((m) => (
          <div
            key={m.id}
            className="px-5 py-3 border-b border-[var(--app-line)] flex items-center gap-3"
            data-testid={`member-${m.id}`}
          >
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium truncate">{m.name || m.email}</div>
              <div className="text-xs text-[var(--app-text-muted)] truncate">{m.email}</div>
            </div>
            {canManage && m.id !== user?.id ? (
              <Select value={m.role} onValueChange={(v) => changeRole(m.id, v)}>
                <SelectTrigger
                  className={`w-44 h-8 text-xs rounded-full ${ROLE_TONE[m.role] || ""}`}
                  data-testid={`role-select-${m.id}`}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {roles.map((r) => (
                    <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-1 rounded-full ${ROLE_TONE[m.role] || ""}`}>
                {roles.find((r) => r.key === m.role)?.label || m.role}
              </span>
            )}
            {canManage && m.id !== user?.id && (
              <button
                onClick={() => remove(m.id, m.email)}
                data-testid={`remove-${m.id}`}
                className="text-[var(--app-text-muted)] hover:text-red-600"
                aria-label="Remove"
              >
                <Trash2 size={14} />
              </button>
            )}
          </div>
        ))}
      </div>

      {canManage && invites.length > 0 && (
        <div className="card-flat mt-6" data-testid="pending-invites">
          <div className="px-5 py-3 border-b border-[var(--app-line)] text-[10px] uppercase tracking-[0.2em] text-[var(--app-text-muted)]">
            Pending invites ({invites.length})
          </div>
          {invites.map((i) => (
            <div
              key={i.id}
              className="px-5 py-3 border-b border-[var(--app-line)] flex items-center gap-3"
              data-testid={`invite-${i.id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{i.email}</div>
                <div className="text-xs text-[var(--app-text-muted)]">
                  {roles.find((r) => r.key === i.role)?.label || i.role} · invited{" "}
                  {new Date(i.created_at).toLocaleDateString()}
                </div>
              </div>
              <span className="text-[10px] uppercase tracking-[0.15em] px-2 py-1 rounded-full bg-amber-50 text-amber-700">
                Pending
              </span>
            </div>
          ))}
        </div>
      )}

      <RolePermissionsCard currentRole={role || user?.role} />
    </div>
  );
}

// ---------------------------------------------------------------------------
const ROLE_MATRIX = [
  { label: "Property Lookup & Offers", admin: true, acquisition_manager: true, lead_manager: true, cold_caller: "view" },
  { label: "CRM Pipeline",             admin: true, acquisition_manager: true, lead_manager: true, cold_caller: true },
  { label: "Delete leads",             admin: true, acquisition_manager: true, lead_manager: false, cold_caller: false },
  { label: "Marketing campaigns (send)", admin: true, acquisition_manager: true, lead_manager: true, cold_caller: false },
  { label: "SMS Blaster (bulk send)",  admin: true, acquisition_manager: true, lead_manager: false, cold_caller: false },
  { label: "Data Lists / Skip Trace",  admin: true, acquisition_manager: true, lead_manager: true, cold_caller: false },
  { label: "Dispo Suite (send to buyers)", admin: true, acquisition_manager: true, lead_manager: true, cold_caller: false },
  { label: "Team (invite / remove)",   admin: true, acquisition_manager: false, lead_manager: false, cold_caller: false },
  { label: "Admin Dashboard",          admin: true, acquisition_manager: false, lead_manager: false, cold_caller: false },
  { label: "Billing & Universal Key",  admin: true, acquisition_manager: true, lead_manager: false, cold_caller: false },
  { label: "Settings / Buyer Profile", admin: true, acquisition_manager: false, lead_manager: false, cold_caller: false },
];

const ROLE_COLS = ["admin", "acquisition_manager", "lead_manager", "cold_caller"];
const ROLE_COL_LABELS = {
  admin: "Admin",
  acquisition_manager: "Acq. Manager",
  lead_manager: "Lead Manager",
  cold_caller: "Cold Caller",
};

function Cell({ v }) {
  if (v === true) return <span className="text-emerald-600">✓</span>;
  if (v === false) return <span className="text-red-500/60">·</span>;
  return <span className="text-[10px] uppercase tracking-widest text-amber-700">{v}</span>;
}

function RolePermissionsCard({ currentRole }) {
  return (
    <div className="card-flat mt-8" data-testid="role-matrix">
      <div className="px-5 py-3 border-b border-[var(--app-line)]">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--app-text-muted)]">
          Role permissions
        </div>
        <div className="text-sm mt-1">
          What each role can do. Your current role: <strong>{ROLE_COL_LABELS[currentRole] || currentRole || "—"}</strong>.
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[var(--app-line)]">
              <th className="text-left px-5 py-2 text-[10px] uppercase tracking-[0.15em] text-[var(--app-text-muted)] font-normal">Capability</th>
              {ROLE_COLS.map((r) => (
                <th key={r}
                    className={`text-center px-3 py-2 text-[10px] uppercase tracking-[0.15em] font-normal ${
                      r === currentRole ? "text-[var(--app-black)] bg-[var(--app-surface)]" : "text-[var(--app-text-muted)]"
                    }`}
                >
                  {ROLE_COL_LABELS[r]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ROLE_MATRIX.map((row) => (
              <tr key={row.label} className="border-b border-[var(--app-line)] last:border-0">
                <td className="px-5 py-2 text-sm">{row.label}</td>
                {ROLE_COLS.map((r) => (
                  <td key={r}
                      className={`text-center px-3 py-2 ${
                        r === currentRole ? "bg-[var(--app-surface)]" : ""
                      }`}
                  >
                    <Cell v={row[r]} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
